import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { RootState } from '../../app/store';
import { connect, ConnectedProps } from 'react-redux';
import {
  StyledDropDownListTransparent,
  StyledLabel,
  StyledMultiSelect,
} from '../../components/FormField/styles';
import {
  setListExercise,
  setListTestDefinitions,
  setListTests,
  setListUsers,
  setLitTestResults,
  setSelectedColumns,
  setSelectedDefinition,
  setSelectedExercise,
  setSelectedSummaryColumns,
  setSelectedUser,
  toggleAllTests,
  toggleCheckTest,
} from './TestResults.slice';
import ResultsService from '../../service/Results.service';
import { Test, TestResults } from '../../domains/results';
import ChartResult from './components/ChartResult/ChartResult.component';
import Moment from 'react-moment';
import moment from 'moment';
import { StyledTable, Wrap } from '../../assets/styledAssets/grid';
import { fontColorSecond } from '../../assets/styledAssets/vars';
import Button from '../../components/Button/Button.component';
import { GridCellProps, GridColumn } from '@progress/kendo-react-grid';
import { StyledTD } from '../Questionnaires/QuestionnairesList/styles';
import PersonsService from '../../service/Persons.service';
import SpaceService from '../../service/Space.service';
import { useIntl } from 'react-intl';
import messages from './messages';
import {
  ICommunication,
  receiveMessage,
  sendMessage,
} from '../../helpers/communication.helper';
import useDidUpdateEffect from '../../hooks/useDidUpdate';
import { ResultProperty } from './testresults';
import { Gender, ListResponse, Roles } from '../../domains/app';
import { StyledLoader } from '../../assets/styledAssets/loader';
import { selectLanguage } from '../LanguageProvider/LanguageProvider.selectors';

const connector = connect(
  (state: RootState) => ({
    listUsers: state.testResults.listUsers,
    listExercise: state.testResults.listExercise,
    listTests: state.testResults.listTests,
    selectedColumns: state.testResults.selectedColumns,
    selectedSummaryColumns: state.testResults.selectedSummaryColumns,
    selectedUser: state.testResults.selectedUser,
    selectedExercise: state.testResults.selectedExercise,
    listTestDefinitions: state.testResults.listTestDefinitions,
    litTestResults: state.testResults.litTestResults,
    selectedDefinition: state.testResults.selectedDefinition,
    selectedAccount: state.app.selectedAccount,
    user: state.app.user,
    selectedLanguage: selectLanguage(state),
  }),
  {
    setListUsers,
    setListExercise,
    setListTests,
    setListTestDefinitions,
    toggleCheckTest,
    toggleAllTests,
    setSelectedColumns,
    setSelectedSummaryColumns,
    setSelectedUser,
    setSelectedExercise,
    setLitTestResults,
    setSelectedDefinition,
  },
);

type PropsFromRedux = ConnectedProps<typeof connector>;

const TestResultsView: FunctionComponent<PropsFromRedux> = ({
  listUsers,
  listExercise,
  setListUsers,
  setListExercise,
  setListTests,
  listTests,
  toggleCheckTest,
  toggleAllTests,
  selectedColumns,
  setSelectedColumns,
  selectedSummaryColumns,
  setSelectedSummaryColumns,
  selectedUser,
  selectedExercise,
  litTestResults,
  setSelectedUser,
  setSelectedExercise,
  setLitTestResults,
  selectedAccount,
  user,
  setListTestDefinitions,
  listTestDefinitions,
  setSelectedDefinition,
  selectedDefinition,
  selectedLanguage,
}) => {
  const [listRawProperties, setListRawProperties] = useState<ResultProperty[]>(
    [],
  );
  const [listAnalysisProperties, setListAnalysisProperties] = useState<
    ResultProperty[]
  >([]);
  const [listAvgProperties, setListAvgProperties] = useState<ResultProperty[]>(
    [],
  );
  const checkIntervalId = useRef<NodeJS.Timeout>();
  const spreadsheetTab = useRef<Window>();
  const checkedResultsRef = useRef<TestResults[]>([]);
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const intl = useIntl();

  const countSelectedTests = useMemo(
    () => (listTests?.filter((e) => e.checked) || []).length,
    [listTests],
  );
  const checkedResults: TestResults[] = useMemo(() => {
    const selectedTests = listTests?.filter((e) => e.checked).map((e) => e.id);
    return litTestResults?.filter((result) =>
      selectedTests.includes(result.id),
    );
    // eslint-disable-next-line
  }, [countSelectedTests, litTestResults]);

  const processResponse = useCallback(
    (m: ICommunication) => {
      if (m.command === 'loaded') {
        if (checkIntervalId.current) {
          clearInterval(checkIntervalId.current);
          checkIntervalId.current = undefined;
        }
        sendMessage({
          command: 'list-sheets',
          data: checkedResultsRef.current,
        });
      }
      if (m.command === 'tab-closed') {
        spreadsheetTab.current = undefined;
      }
    },
    [checkedResultsRef],
  );

  useEffect(() => {
    if (user && selectedAccount && selectedAccount.role & Roles.User) {
      setListUsers([user]);
      setSelectedUser([user]);
    } else if (
      selectedAccount &&
      selectedAccount.role & (Roles.SpaceOwner | Roles.SpaceAdministrator)
    ) {
      SpaceService.fetchListMembers(
        selectedAccount?.id as string,
        1000,
        0,
      ).then((res) => setListUsers(res.items.map((it) => it.person)));
    } else {
      PersonsService.fetchListUser(1000, 0).then((res) =>
        setListUsers(res.items),
      );
    }

    const processCommunication = function () {
      receiveMessage(arguments[0], processResponse);
    };
    window.addEventListener('storage', processCommunication);
    return () => {
      window.removeEventListener('storage', processCommunication);
    };

    // eslint-disable-next-line
  }, []);

  useDidUpdateEffect(() => {
    ResultsService.getTestTypesByPersons(
      selectedUser.map((u) => u.id as string),
    ).then((res) => {
      setListExercise(res);
    });
  }, [selectedUser]);

  useDidUpdateEffect(() => {
    if (selectedExercise?.id && selectedUser) {
      const listRequests: Promise<ListResponse<Test>>[] = [];
      selectedUser.forEach((user) =>
        listRequests.push(
          ResultsService.fetchListTests(selectedExercise.id, user.id as string),
        ),
      );

      Promise.all(listRequests).then((res) => {
        const tests = res.flatMap((r) => r.items);
        const setStr = new Set(tests.map((t) => t.testDefinition));
        setListTestDefinitions(Array.from(setStr));
        setSelectedDefinition('');
      });
    }
    // eslint-disable-next-line
  }, [selectedExercise?.id, selectedUser]);

  useEffect(() => {
    if (selectedExercise?.id && selectedUser && selectedDefinition) {
      const listRequests: Promise<ListResponse<Test>>[] = [];
      selectedUser.forEach((user) =>
        listRequests.push(
          ResultsService.fetchListTests(
            selectedExercise.id,
            user.id as string,
            selectedDefinition,
          ),
        ),
      );

      Promise.all(listRequests).then((res) => {
        const tests = res.flatMap((r) => r.items);
        setListTests(tests);
      });
    } else {
      setListTests([]);
    }
    // eslint-disable-next-line
  }, [selectedDefinition]);

  useEffect(() => {
    const listRequests: Promise<TestResults>[] = [];
    listTests.forEach((test) => {
      const loadedResult = litTestResults.find((r) => r.id === test.id);
      if (!loadedResult) {
        listRequests.push(
          ResultsService.fetchTestResults(
            test.id,
            selectedLanguage?.id as string,
          ),
        );
      }
    });

    setShowLoader(true);
    Promise.all(listRequests)
      .then((res) => {
        res.forEach((test) => {
          const user = listUsers.find((u) => u.id === test.mlCloudMemberId);
          test.personName = user?.firstName + ' ' + user?.lastName;
        });
        setLitTestResults(litTestResults.concat(res));
      })
      .finally(() => {
        setShowLoader(false);
      });
    // eslint-disable-next-line
  }, [listTests, setLitTestResults]);

  useEffect(() => {
    let tempListProps: ResultProperty[] = [];
    const testRes = litTestResults.find(
      (r) => r.mlCloudTestTypeId === selectedExercise?.id,
    );
    if (testRes) {
      const rawProperties = testRes.raw?.properties || [];
      const rawPropertiesTemp: ResultProperty[] = [];
      rawProperties.forEach((prop) => {
        if (!(prop.mlPropertyNameId === 'tStp')) {
          tempListProps.push({
            name: prop.name,
            description: 'Raw data',
            shortName: prop.mlPropertyNameId,
          });
          rawPropertiesTemp.push({
            name: prop.name,
            description: 'Raw data',
            shortName: prop.mlPropertyNameId,
          });
        }
      });
      setListRawProperties(rawPropertiesTemp);

      const analysisProperties = testRes.analysis?.properties || [];
      const analysisPropertiesTemp: ResultProperty[] = [];
      analysisProperties.forEach((prop) => {
        if (
          !(
            prop.mlPropertyNameId === 'tStp' || prop.mlPropertyNameId === 'Side'
          )
        ) {
          tempListProps.push({
            name: prop.name,
            description: 'Analysis data',
            shortName: prop.mlPropertyNameId,
          });
          analysisPropertiesTemp.push({
            name: prop.name,
            description: 'Analysis data',
            shortName: prop.mlPropertyNameId,
          });
        }
      });
      setListAnalysisProperties(analysisPropertiesTemp);
    }

    const newSelectedColumns = selectedColumns.filter((col) =>
      tempListProps.map((p) => p.name).includes(col.name),
    );
    if (newSelectedColumns.length !== selectedColumns.length) {
      setSelectedColumns(newSelectedColumns);
    }

    let tempAvgListProps: ResultProperty[] = [];
    if (testRes) {
      (testRes.summary?.properties || [])
        .filter((prop) =>
          (prop.values as any[]).every((v: any) => typeof v === 'number'),
        )
        .forEach((prop) => {
          tempAvgListProps.push({
            name: prop.name,
            description: 'Summary data',
            shortName: prop.mlPropertyNameId,
          });
        });
    }
    setListAvgProperties(tempAvgListProps);
    const newSelectedSumColumns = selectedSummaryColumns.filter((col) =>
      tempAvgListProps.map((p) => p.name).includes(col.name),
    );
    if (newSelectedSumColumns.length !== selectedSummaryColumns.length) {
      setSelectedSummaryColumns(newSelectedSumColumns);
    }
    // eslint-disable-next-line
  }, [litTestResults]);

  useEffect(() => {
    checkedResultsRef.current = checkedResults;
    // eslint-disable-next-line
  }, [checkedResults]);

  const handleHeaderCheck = (e: any) => {
    const checked = e.syntheticEvent.target.checked;
    toggleAllTests(checked);
  };

  /*  const getTestResult = useCallback(
    (id: string) => {
      return litTestResults?.find((t) => t.id === id);
    },
    [litTestResults],
  );

  const loadTestResult = (id: string) => {
    const loadedResult = litTestResults.find((r) => r.id === id);
    if (!loadedResult) {
      ResultsService.fetchTestResults(id, selectedLanguage?.id as string).then(
        (res) => {
          setLitTestResults(litTestResults.concat([res]));
        },
      );
    }
  };*/

  const openSpreadsheet = () => {
    if (!spreadsheetTab.current) {
      const tab = window.open(window.location.origin + '/spreadsheet');
      if (tab) {
        tab.addEventListener('DOMContentLoaded', () => {
          checkIntervalId.current = setInterval(() => {
            sendMessage({
              command: 'check-loading',
            });
          }, 300);
        });
        spreadsheetTab.current = tab;
      }
    } else {
      sendMessage({
        command: 'list-sheets',
        data: checkedResults,
      });
      spreadsheetTab.current?.focus();
    }
  };

  return (
    <Wrap overflow={'auto'} style={{ position: 'relative' }}>
      <Wrap
        width={'100%'}
        alignItems={'center'}
        justifyCont={'space-between'}
        margin={'0 0 4rem 0'}
      >
        <Wrap
          flexWrap={'nowrap'}
          flexDir={['column', 'row']}
          alignItems={['flex-start', 'flex-end']}
        >
          <Wrap alignItems={'flex-end'}>
            <Wrap flexDir={'column'}>
              <StyledLabel>{intl.formatMessage(messages.member)}</StyledLabel>
              {selectedAccount && !(selectedAccount.role & Roles.User) && (
                <StyledMultiSelect
                  data={listUsers}
                  textField="fullName"
                  dataItemKey="id"
                  value={selectedUser}
                  onChange={(event: any) => setSelectedUser(event.target.value)}
                />
              )}
              {selectedAccount && !!(selectedAccount.role & Roles.User) && (
                <Wrap padding={'1rem 0'}>{selectedUser[0]?.fullName}</Wrap>
              )}
            </Wrap>
            <Wrap
              fs={'2.4rem'}
              color={fontColorSecond}
              margin={'0 1.5rem .7rem 1.5rem'}
            >
              /
            </Wrap>
          </Wrap>
          <Wrap alignItems={'flex-end'} margin={['2rem 0', '0']}>
            <Wrap flexDir={'column'}>
              <StyledLabel>{intl.formatMessage(messages.exercise)}</StyledLabel>
              <StyledDropDownListTransparent
                data={listExercise}
                textField="name"
                dataItemKey="id"
                value={selectedExercise}
                onChange={(event: any) =>
                  setSelectedExercise(event.target.value)
                }
              />
            </Wrap>
            <Wrap
              fs={'2.4rem'}
              color={fontColorSecond}
              margin={'0 1.5rem .7rem 1.5rem'}
            >
              /
            </Wrap>
          </Wrap>
          <Wrap flexDir={'column'}>
            <StyledLabel>{intl.formatMessage(messages.definition)}</StyledLabel>
            <StyledDropDownListTransparent
              data={listTestDefinitions}
              value={selectedDefinition}
              onChange={(event: any) =>
                setSelectedDefinition(event.target.value)
              }
            />
          </Wrap>
        </Wrap>
        <Wrap margin={['2rem 0 0 0', '0']}>
          <Button primary onClick={openSpreadsheet}>
            {intl.formatMessage(messages.sendToSpreadSheet)}
          </Button>
        </Wrap>
      </Wrap>

      <Wrap
        grow={'1'}
        margin={'2rem 0'}
        overflow={'hidden'}
        width={'100%'}
        height={'330px'}
        hiddenXS
      >
        <StyledTable
          style={{ height: '100%', maxHeight: '330px' }}
          data={listTests}
          onRowClick={(e) => toggleCheckTest(e.dataItem.id)}
          onSelectionChange={(e) => toggleCheckTest(e.dataItem.id)}
          selectedField="checked"
          onHeaderSelectionChange={handleHeaderCheck}
        >
          <GridColumn field={'checked'} width="50px" />
          <GridColumn
            field="date"
            title={intl.formatMessage(messages.date)}
            width="130px"
            cell={(props: GridCellProps) => (
              <td>
                <div onClick={() => toggleCheckTest(props.dataItem.id)}>
                  {moment(props.dataItem.date).format('DD MMM YYYY')}
                </div>
              </td>
            )}
          />
          <GridColumn
            field="date"
            title={intl.formatMessage(messages.time)}
            width="100px"
            cell={(props: GridCellProps) => (
              <td>
                <div onClick={() => toggleCheckTest(props.dataItem.id)}>
                  {moment(props.dataItem.date).format('HH:mm:ss')}
                </div>
              </td>
            )}
          />
          <GridColumn
            field="person.fullName"
            title={intl.formatMessage(messages.member)}
            width="200px"
          />
          <GridColumn
            field="person.pid"
            title={intl.formatMessage(messages.pid)}
          />
          <GridColumn
            field="person.fid"
            title={intl.formatMessage(messages.fid)}
          />
          <GridColumn
            field="person.gender"
            title={intl.formatMessage(messages.gender)}
            cell={(props: GridCellProps) => (
              <td>
                {intl.formatMessage(
                  props.dataItem.person.gender === Gender.Male
                    ? messages.male
                    : messages.female,
                )}
              </td>
            )}
          />
          <GridColumn
            field="person.bodyWeight"
            title={intl.formatMessage(messages.bodyWeight)}
            format={'{0:format} kg'}
          />
          <GridColumn
            field="person.birthDate"
            title={intl.formatMessage(messages.age)}
            cell={(props: GridCellProps) => (
              <td>
                {moment().diff(props.dataItem.person.birthDate, 'years', false)}
              </td>
            )}
          />
          <GridColumn
            field="side"
            title={intl.formatMessage(messages.side)}
            width="100px"
          />
          <GridColumn
            field="technology"
            width="200px"
            title={intl.formatMessage(messages.technology)}
          />
          <GridColumn
            field="location"
            width="200px"
            title={intl.formatMessage(messages.location)}
          />
          <GridColumn
            field="additionalInfo"
            title={intl.formatMessage(messages.additionalInfo)}
            width="200px"
            cell={(props) => (
              <StyledTD>{props.dataItem.additionalInfo}</StyledTD>
            )}
          />
        </StyledTable>
      </Wrap>

      <ChartResult
        chartData={checkedResults}
        selectedColumns={selectedColumns}
        setSelectedColumns={setSelectedColumns}
        listProperties={listRawProperties}
        listSecondProperties={listAnalysisProperties}
        propertyName={intl.formatMessage(messages.rowProperties)}
        secondPropertyName={intl.formatMessage(messages.analysisProperties)}
      />

      <Wrap
        grow={'1'}
        margin={'2rem 0'}
        overflow={'hidden'}
        width={'100%'}
        height={'330px'}
        hiddenSM
      >
        <StyledTable
          style={{ height: '100%', maxHeight: '500px' }}
          data={listTests}
          onRowClick={(e) => toggleCheckTest(e.dataItem.id)}
          onSelectionChange={(e) => toggleCheckTest(e.dataItem.id)}
          selectedField="checked"
          onHeaderSelectionChange={handleHeaderCheck}
        >
          <GridColumn field={'checked'} width="50px" />
          <GridColumn
            field="date"
            title={intl.formatMessage(messages.results)}
            cell={(props: GridCellProps) => (
              <td>
                <Wrap
                  width={'100%'}
                  alignItems={'center'}
                  margin={'0 0 2rem 0'}
                >
                  <Wrap width={'calc(50% - 1rem)'} flexDir={'column'}>
                    <Wrap fs={'1.4rem'} color={fontColorSecond}>
                      {intl.formatMessage(messages.results)}
                    </Wrap>
                    <Wrap
                      onClick={() => toggleCheckTest(props.dataItem.id)}
                      fw={'bold'}
                    >
                      <Moment format={'DD MMM YYYY'}>
                        {props.dataItem.date}
                      </Moment>
                    </Wrap>
                  </Wrap>
                  <Wrap width={'calc(50% - 1rem)'} flexDir={'column'}>
                    <Wrap fs={'1.4rem'} color={fontColorSecond}>
                      {intl.formatMessage(messages.time)}
                    </Wrap>
                    <Wrap
                      onClick={() => toggleCheckTest(props.dataItem.id)}
                      fw={'bold'}
                    >
                      <Moment format={'HH:mm:ss'}>{props.dataItem.date}</Moment>
                    </Wrap>
                  </Wrap>
                </Wrap>
                <Wrap margin={'0 0 2rem 0'} width={'100%'} flexDir={'column'}>
                  <Wrap fs={'1.4rem'} color={fontColorSecond}>
                    {intl.formatMessage(messages.technology)}
                  </Wrap>
                  <Wrap fw={'bold'}>{props.dataItem.technology}</Wrap>
                </Wrap>
                <Wrap margin={'0 0 2rem 0'} width={'100%'} flexDir={'column'}>
                  <Wrap fs={'1.4rem'} color={fontColorSecond}>
                    {intl.formatMessage(messages.additionalInfo)}
                  </Wrap>
                  <Wrap fw={'bold'}>{props.dataItem.additionalInfo}</Wrap>
                </Wrap>
                <Wrap width={'100%'} flexDir={'column'}>
                  <Wrap fs={'1.4rem'} color={fontColorSecond}>
                    {intl.formatMessage(messages.location)}
                  </Wrap>
                  <Wrap fw={'bold'}>{props.dataItem.location}</Wrap>
                </Wrap>
              </td>
            )}
          />
        </StyledTable>
      </Wrap>

      <ChartResult
        chartData={checkedResults}
        selectedColumns={selectedSummaryColumns}
        setSelectedColumns={setSelectedSummaryColumns}
        listProperties={listAvgProperties}
        chartCategory={'summary'}
        propertyName={intl.formatMessage(messages.summaryProperties)}
      />

      {showLoader && <StyledLoader />}
    </Wrap>
  );
};

export default connector(TestResultsView);
